import { useEffect, useState, useSyncExternalStore } from "react";
import { rolesApi, type RoleCatalogEntry } from "@/lib/api/roles";
import { Role } from "@/lib/constants/roles";
import { useAuthStore } from "@/store/authStore";

/**
 * The roles the server lets read the catalog: the user directory's viewers
 * (`IsUserDirectoryViewer`). Anyone else is refused with a 403, so they are
 * not sent to ask, on every page, for something they cannot have. Their
 * labels fall back to the humanised code and their permissions to the
 * baseline `usePermissions` carries.
 */
const CATALOG_READERS: ReadonlySet<string> = new Set([
  Role.PLATFORM_ADMIN,
  Role.PLATFORM_MANAGER,
  Role.DISTRIBUTOR_ADMIN,
  Role.MSP_ADMIN,
  Role.COMPANY_OWNER,
  Role.COMPANY_ADMIN,
]);

// Module-scoped cache — the catalog is static server-side (changes only when
// `User.ROLE_CHOICES` changes), so fetching once per session is sufficient.
// Concurrent callers share the same in-flight promise to avoid duplicate
// requests during initial render.
let cache: RoleCatalogEntry[] | null = null;
let inflight: Promise<RoleCatalogEntry[]> | null = null;
const listeners = new Set<() => void>();

async function loadCatalog(): Promise<RoleCatalogEntry[]> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = rolesApi
    .catalog()
    .then((data) => {
      cache = data;
      listeners.forEach((notify) => notify());
      return data;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/**
 * `platform_admin` -> "Platform Admin".
 *
 * Exported so the few places that render a role without the hook (toasts)
 * share one spelling instead of hardcoding their own — which is how "System
 * Admin" survived the role rename in three separate files.
 */
export function humaniseRoleCode(code: string): string {
  return code
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export interface UseRoleCatalogResult {
  catalog: RoleCatalogEntry[];
  byCode: Map<string, RoleCatalogEntry>;
  labelOf: (code: string | undefined | null) => string;
  isAdminTier: (code: string | undefined | null) => boolean;
  isLoading: boolean;
}

/**
 * React hook for the server-authoritative role catalog.
 *
 * Returns labels, admin-tier classification, and a code→entry Map. All three
 * derive from the backend's ``User.ROLE_CHOICES`` + ``ROLE_LEVEL`` tables so
 * the frontend never has to maintain a parallel list.
 *
 * `labelOf` gracefully falls back to the raw code (or an empty string) so
 * components can render something while the first fetch is in flight.
 */
export function useRoleCatalog(): UseRoleCatalogResult {
  const { user } = useAuthStore();
  const mayRead = user?.role != null && CATALOG_READERS.has(user.role);
  const [catalog, setCatalog] = useState<RoleCatalogEntry[]>(cache ?? []);
  const [isLoading, setIsLoading] = useState<boolean>(
    cache === null && mayRead,
  );

  useEffect(() => {
    let cancelled = false;
    if (cache) {
      setCatalog(cache);
      setIsLoading(false);
      return;
    }
    if (!mayRead) {
      setIsLoading(false);
      return;
    }
    loadCatalog()
      .then((data) => {
        if (!cancelled) {
          setCatalog(data);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mayRead]);

  const byCode = new Map(catalog.map((r) => [r.code, r]));

  const labelOf = (code: string | undefined | null): string => {
    if (!code) return "";
    // Humanise rather than returning the raw code. The catalog is fetched, so
    // it is absent on the first paint of any page, and falling back to
    // `platform_admin` put a snake_case code in front of the user there.
    // Every current code title-cases correctly — `platform_admin` reads
    // "Platform Admin" — so the fallback matches the server's own labels.
    return byCode.get(code)?.label ?? humaniseRoleCode(code);
  };

  const isAdminTier = (code: string | undefined | null): boolean => {
    if (!code) return false;
    return byCode.get(code)?.is_admin_tier ?? false;
  };

  return { catalog, byCode, labelOf, isAdminTier, isLoading };
}

/**
 * The catalog once something has loaded it, else null; never fetches.
 *
 * For readers that must not start a request of their own (`usePermissions`
 * runs everywhere, including tests, where a fetch would toast a network
 * error). The layout's `useRoleCatalog` loads it, and every reader of
 * this re-renders when it lands.
 */
export function useLoadedRoleCatalog(): RoleCatalogEntry[] | null {
  return useSyncExternalStore(
    (notify) => {
      listeners.add(notify);
      return () => listeners.delete(notify);
    },
    () => cache,
  );
}
